import React, { useEffect, useState } from "react";
import { Check, ChevronDown, History, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";

let cache = null;
export const resetTabStatusCache = () => { cache = null; };

export function useTabStatusConfig() {
  const [tabs, setTabs] = useState({});
  useEffect(() => {
    cache = cache || api.get("/configuration/tab-statuses").then((r) => Object.fromEntries(r.data.tabs.map((t) => [t.key, t.statuses])));
    cache.then(setTabs).catch(() => { cache = null; });
  }, []);
  return tabs;
}

export function StatusChip({ status, label, className = "" }) {
  const color = status?.color || "#94A3B8";
  return (
    <span className={`inline-flex items-center gap-1 text-[9px] leading-none uppercase tracking-wider font-semibold px-1.5 py-[3px] rounded border whitespace-nowrap ${className}`}
      style={{ color, borderColor: color, backgroundColor: `${color}14` }}>
      {status?.final && <CheckCircle2 className="w-2.5 h-2.5" />}
      {label || status?.label || "À faire"}
    </span>
  );
}

export function TabStatusPicker({ statuses = [], value, onChange, testid }) {
  const current = statuses.find((s) => s.id === value) || statuses.find((s) => s.id === "pending") || statuses[0];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="inline-flex items-center gap-0.5 focus:outline-none min-w-0 max-w-full" title={`${current?.label || ""} — changer le statut de l'onglet`} data-testid={testid}>
          <StatusChip status={current} className="min-w-0 max-w-full overflow-hidden" />
          <ChevronDown className="w-2.5 h-2.5 text-norea-textMuted" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[180px]" data-testid={`${testid}-menu`}>
        {statuses.map((s) => (
          <DropdownMenuItem key={s.id} onSelect={() => s.id !== current?.id && onChange(s.id)} className="flex items-center justify-between gap-3 text-xs" data-testid={`${testid}-option-${s.id}`}>
            <span className="inline-flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: s.color }} />
              {s.label}
              {s.final && <span className="text-[9px] uppercase tracking-wider text-norea-textMuted">final</span>}
            </span>
            {s.id === current?.id && <Check className="w-3.5 h-3.5 text-norea-primary" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const fmt = (iso) => new Date(iso).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });

export function TabStatusHistory({ dossierId, tab, statuses = [], value, onChange, refreshKey }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    api.get(`/dossiers/${dossierId}/tab-status-history/${tab}`).then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, [dossierId, tab, refreshKey]);
  const current = statuses.find((s) => s.id === value);
  const byLabel = Object.fromEntries(statuses.map((s) => [s.label, s]));
  const byId = Object.fromEntries(statuses.map((s) => [s.id, s]));
  const pick = (id, label) => byId[id] || byLabel[label];
  return (
    <div className="mb-4 rounded-lg border border-norea-border bg-white px-4 py-2.5" data-testid={`tab-status-block-${tab}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs text-norea-textMuted">
          <span className="font-medium text-norea-text">Statut de l'onglet</span>
          <TabStatusPicker statuses={statuses} value={value} onChange={onChange} testid={`tab-panel-status-${tab}`} />
          {current?.final && <span className="text-emerald-700 inline-flex items-center gap-1" data-testid={`tab-final-${tab}`}><CheckCircle2 className="w-3.5 h-3.5" />Onglet terminé</span>}
        </div>
        <button type="button" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1 text-xs text-norea-textMuted hover:text-norea-text" data-testid={`tab-status-history-toggle-${tab}`}>
          <History className="w-3.5 h-3.5" /> Historique ({items.length})
          <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {open && (
        <ul className="mt-2.5 pt-2.5 border-t border-norea-border space-y-1.5" data-testid={`tab-status-history-${tab}`}>
          {items.length === 0 && <li className="text-xs italic text-norea-textMuted">Aucun changement de statut pour l'instant.</li>}
          {items.map((h) => (
            <li key={h.id} className="flex items-center gap-2 text-xs text-norea-textMuted flex-wrap" data-testid={`tab-status-history-item-${h.id}`}>
              <span className="tabular-nums w-28 shrink-0">{fmt(h.created_at)}</span>
              <strong className="text-norea-primary">@{(h.user_email || "system").split("@")[0]}</strong>
              {h.before ? <StatusChip status={pick(h.before_id, h.before)} label={h.before} /> : <StatusChip status={byId.pending} />}
              <span>→</span>
              <StatusChip status={pick(h.after_id, h.after)} label={h.after} />
              {h.label?.includes("remplacé") && <span className="italic">(statut supprimé, remplacé)</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
